import { z } from '../zod.js';
import { DashboardUrlField } from '../shared/dashboardUrl';
import { PaginatedListSchema } from '../shared/paginatedList';
import { PaymentMethodValidationSchema } from '../shared/paymentMethodValidationSchema';
import {
  SUBSCRIPTION_PAYMENT_PAYMENT_STATUSES,
  SUBSCRIPTION_PAYMENT_INVOICE_STATUSES,
  SUBSCRIPTION_PAYMENT_BILLING_REASONS,
} from './list';

export const SubscriptionPaymentResponseValidationSchema = z.object({
  id: z.string().meta({
    description: 'Unique subscription payment record ID. Use this value for pagination cursors.',
  }),
  subscriptionId: z.string().nullish().meta({
    description:
      'ID of the parent subscription. Use with `list_subscriptions` for the parent subscription record (status, plan, trial, coupon).',
  }),
  customerId: z.string().nullish().meta({
    description: 'Customer ID.',
  }),
  pageId: z.string().nullish().meta({
    description: 'Page ID.',
  }),
  productId: z.string().nullish().meta({
    description: 'Product ID.',
  }),
  sellerId: z.string().nullish(),
  transactionIds: z.array(z.string()).nullish().meta({
    description:
      "IDs of the underlying ledger transactions associated with this payment (e.g. the original charge, plus refund or adjustment entries). Useful for reconciling a single payment against the seller's transaction ledger.",
  }),
  customerEmail: z.string().nullish().meta({
    description:
      'Customer email address. Snapshot captured when this payment record was created — may differ from the current customer record if the customer was updated later. May be missing on pre-backfill records.',
  }),
  customerName: z.string().nullish().meta({
    description:
      'Customer full name. Snapshot captured when this payment record was created — may differ from the current customer record if the customer was updated later. May be missing on pre-backfill records.',
  }),
  productTitle: z.string().nullish().meta({
    description:
      'Human-readable product or plan name. Snapshot captured when this payment record was created — may differ from the current product record if the product was updated later. May be missing on pre-backfill records.',
  }),
  pageSlug: z.string().nullish().meta({
    description:
      'Page slug. Snapshot captured when this payment record was created — may differ from the current page record if the slug was changed later. May be missing on pre-backfill records.',
  }),
  currency: z.string().nullish().meta({
    description: 'Three-letter currency code.',
  }),
  amount: z.number().meta({
    description:
      'Payment amount in the smallest currency unit (e.g. `10661` USD = `$106.61`, `299` EUR = `€2.99`, `500` JPY = `¥500`). Divide by 100 before displaying to merchants or customers, noting that some currencies such as JPY do not use decimal minor units. To total what was collected for a subscription, sum `amount` across records where `paid: true`.',
  }),
  amountUsd: z.number().nullish().meta({
    description:
      'USD-converted payment amount for multi-currency accounts. Same smallest-currency-unit convention as `amount` (divide by 100 before displaying USD).',
  }),
  livemode: z.boolean().nullish().meta({
    description:
      '`true` for live charges, `false` for test mode. Pass `livemode: false` to the list or get tool to read test-mode subscription payments.',
  }),
  paymentStatus: z
    .enum(SUBSCRIPTION_PAYMENT_PAYMENT_STATUSES)
    .nullish()
    .meta({
      description: `Status of the underlying Stripe charge or payment intent. Valid values: ${SUBSCRIPTION_PAYMENT_PAYMENT_STATUSES.join(', ')}. Use this to check whether a specific payment attempt succeeded or failed. A record can have \`paymentStatus: 'failed'\` (most recent attempt failed) while \`invoiceStatus\` is still \`open\` (invoice not yet settled); a later retry that succeeded will flip both \`paymentStatus\` and \`paid\` to success. For renewal failures, surface available payment method details and advise the customer to update their payment method through the customer portal.`,
    }),
  invoiceStatus: z
    .enum(SUBSCRIPTION_PAYMENT_INVOICE_STATUSES)
    .nullish()
    .meta({
      description: `Status of the Stripe invoice itself (overall invoice lifecycle). Valid values: ${SUBSCRIPTION_PAYMENT_INVOICE_STATUSES.join(', ')}. An invoice may be \`paid\` even if it had a failed attempt followed by a successful retry. Distinct from \`paymentStatus\`, which reflects only the most recent payment attempt.`,
    }),
  paid: z.boolean().nullish().meta({
    description:
      'Whether the invoice was fully collected. Convenience boolean for simple "was this paid?" checks — `true` if the invoice was paid in full, regardless of how many attempts it took.',
  }),
  refunded: z.boolean().nullish().meta({
    description: 'Whether the payment was fully refunded.',
  }),
  partiallyRefunded: z.boolean().nullish().meta({
    description: 'Whether the payment was partially refunded.',
  }),
  billingReason: z
    .enum(SUBSCRIPTION_PAYMENT_BILLING_REASONS)
    .nullish()
    .meta({
      description: `Reason the invoice was generated. Use this to explain to a customer why a charge was generated on a specific date. Valid values: ${SUBSCRIPTION_PAYMENT_BILLING_REASONS.join(', ')}.
\`subscription_create\`: first payment when the subscription is created and activated; the customer's initial charge.
\`subscription_cycle\`: standard renewal at the end of a billing period (monthly, annual, etc.).
\`subscription_update\`: charge from a plan change or proration; can explain an unexpected charge after switching plans.
\`subscription_threshold\`: metered billing usage threshold trigger.
\`manual\`: manually triggered by the merchant.
\`upcoming\`: preview of an upcoming invoice; not yet charged.`,
    }),
  paymentMethod: PaymentMethodValidationSchema.nullish().meta({
    description:
      'Payment method details, including `gateway`, `method`, `brand`, `last4`, `country`, `funding`, and optionally `wallet`.',
  }),
  stripeSubscriptionId: z.string().nullish().meta({
    description:
      'Stripe subscription ID for the parent subscription (Stripe-side identifier, not the Checkout Page `subscriptionId`). Useful when reconciling against records in the Stripe dashboard.',
  }),
  stripePaymentId: z.string().nullish().meta({
    description:
      'Stripe charge ID (`ch_…`) for this payment attempt. Useful when reconciling against records in the Stripe dashboard.',
  }),
  stripePaymentIntentId: z.string().nullish().meta({
    description:
      'Stripe payment intent ID for this charge attempt. Useful for narrowing a disputed-charge lookup when the customer provides this identifier.',
  }),
  stripeInvoiceId: z.string().nullish().meta({
    description:
      'Stripe invoice ID for this billing cycle. Useful for narrowing a disputed-charge lookup when the customer provides this identifier.',
  }),
  stripeInvoicePdf: z.string().nullish().meta({
    description:
      'URL of the Stripe-hosted invoice PDF. Surface this as a downloadable receipt or invoice when a customer or merchant asks for one.',
  }),
  stripeRefundInvoicePdf: z.string().nullish().meta({
    description:
      'URL of the Stripe-hosted refund invoice PDF (credit note). Surface this for refunded payments when the customer asks for documentation of the refund.',
  }),
  createdAt: z.iso.datetime().meta({
    description:
      'ISO 8601 UTC timestamp for when the subscription payment record was created. Match against this to answer "why was I charged on date X?" — pair with `billingReason` to explain the cause.',
  }),
  updatedAt: z.iso.datetime().meta({
    description:
      'ISO 8601 UTC timestamp for when the subscription payment record was last updated.',
  }),
});

export const GetSubscriptionPaymentsResponseValidationSchema = PaginatedListSchema(
  SubscriptionPaymentResponseValidationSchema,
);

export const SubscriptionPaymentDetailResponseValidationSchema = z
  .object({
    data: SubscriptionPaymentResponseValidationSchema,
  })
  .meta({
    id: 'SubscriptionPaymentDetailResponse',
    description: 'Envelope containing one subscription payment.',
  });

export const SubscriptionPaymentToolDataValidationSchema =
  SubscriptionPaymentResponseValidationSchema.extend({
    dashboardUrl: DashboardUrlField.optional(),
  });

export const SubscriptionPaymentDetailToolResponseValidationSchema = z.object({
  data: SubscriptionPaymentToolDataValidationSchema,
});

export const SubscriptionPaymentsToolResponseValidationSchema = PaginatedListSchema(
  SubscriptionPaymentToolDataValidationSchema,
);
